"use client";

import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import { signIn } from "@/lib/auth-client";
import Google from "./svg/google";
import Github from "./svg/github";

function LoginButton({
  provider,
  handleProviderSignIn,
  children,
}: {
  provider: "google" | "github";
  handleProviderSignIn: (provider: "google" | "github") => void;
  children: React.ReactNode;
}) {
  const [pending, setPending] = useState(false);

  const handleClick = async () => {
    setPending(true);
    try {
      await handleProviderSignIn(provider);
    } finally {
      // Reset pending state regardless of success or failure,
      // as signIn might navigate away or throw an error handled by the parent.
      // If it doesn't navigate, we want the button to be active again.
      setPending(false);
    }
  };

  const className = pending ? "cursor-progress" : "hover:cursor-pointer";

  return (
    <Button
      variant={"outline"}
      disabled={pending}
      onClick={handleClick}
      className={className}
    >
      {pending ? <Spinner /> : children}
    </Button>
  );
}

type LoginFormProps = {
  redirectTo: string;
};

export function LoginForm({ redirectTo }: LoginFormProps) {
  const [lastProvider, setLastProvider] = useState<"google" | "github" | null>(
    null,
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const storedProvider = window.localStorage.getItem("last-login-provider");
    if (storedProvider === "google" || storedProvider === "github") {
      setLastProvider(storedProvider);
    }
  }, []);

  const handleProviderSignIn = async (provider: "google" | "github") => {
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem("last-login-provider", provider);
      }
      setLastProvider(provider);
      const { error } = await signIn.social({
        provider,
        callbackURL: redirectTo,
        errorCallbackURL: "/error",
        newUserCallbackURL: redirectTo,
      });

      if (error) {
        toast.error(error.message || "Sign in failed");
      }
    } catch {
      toast.error("An unexpected error occurred");
    }
  };

  return (
    <>
      {lastProvider ? (
        <p className="text-center text-sm text-muted-foreground">
          Last logged in with {lastProvider === "google" ? "Google" : "GitHub"}
        </p>
      ) : null}
      <LoginButton
        provider="google"
        handleProviderSignIn={handleProviderSignIn}
      >
        <Google />
        Login with Google
      </LoginButton>
      <LoginButton
        provider="github"
        handleProviderSignIn={handleProviderSignIn}
      >
        <Github />
        Login with Github
      </LoginButton>
    </>
  );
}
